import { useEffect } from "react";

/**
 * Lightweight SEO hook for a React Router SPA — sets document.title,
 * meta description, and Open Graph/Twitter tags per-page without a runtime
 * dependency on react-helmet.
 *
 * Usage inside a page component:
 *   useSeo({
 *     title: "Autofattura estere · DASABO",
 *     description: "Genera autofatture TD17/TD18 in 30 secondi con l'AI.",
 *     path: "/",
 *   });
 */
const SITE_NAME = "Autofattura by Dasabo";
const SITE_URL = "https://autofattura.dasabo.com";
const OG_IMAGE = "https://autofattura.dasabo.com/logo.png";

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function useSeo({ title, description, path, noindex = false, image, standalone = false } = {}) {
  useEffect(() => {
    const fullTitle = title
      ? (standalone ? title : `${title} · ${SITE_NAME}`)
      : SITE_NAME;
    const url = path ? `${SITE_URL}${path}` : SITE_URL;
    const img = image || OG_IMAGE;

    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "it_IT");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    upsertLink("canonical", url);
  }, [title, description, path, noindex, image, standalone]);
}
